/**
 * useCryptoPrices — Custom hook to fetch live crypto prices via CoinGecko.
 *
 * CoinGecko has full CORS support globally. Free tier allows 10-30 req/min.
 *
 * Maps Binance-style symbols (BTCUSDT, ETHUSDT) to CoinGecko IDs internally.
 *
 * Usage:
 *   const { prices, loading } = useCryptoPrices(['BTCUSDT', 'ETHUSDT']);
 *   prices['BTCUSDT'] = { price (USD), change_24h_pct, high_24h, low_24h, volume_24h }
 */
import { useState, useEffect } from 'react';

// Binance pair → CoinGecko ID
const BINANCE_TO_COINGECKO = {
  BTCUSDT: 'bitcoin',
  ETHUSDT: 'ethereum',
  BNBUSDT: 'binancecoin',
  SOLUSDT: 'solana',
  XRPUSDT: 'ripple',
  ADAUSDT: 'cardano',
  DOGEUSDT: 'dogecoin',
  TRXUSDT: 'tron',
  MATICUSDT: 'polygon-pos',
  POLUSDT: 'polygon-ecosystem-token',
  AVAXUSDT: 'avalanche-2',
  DOTUSDT: 'polkadot',
  LINKUSDT: 'chainlink',
  LTCUSDT: 'litecoin',
  BCHUSDT: 'bitcoin-cash',
  UNIUSDT: 'uniswap',
  ATOMUSDT: 'cosmos',
  FILUSDT: 'filecoin',
  NEARUSDT: 'near',
  APTUSDT: 'aptos',
  ARBUSDT: 'arbitrum',
  OPUSDT: 'optimism',
  PEPEUSDT: 'pepe',
  SHIBUSDT: 'shiba-inu',
};

export const useCryptoPrices = (binanceSymbols = [], intervalMs = 20000) => {
  const [prices, setPrices] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!binanceSymbols || binanceSymbols.length === 0) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    // Build mapping for this fetch
    const ids = [];
    const idToBinance = {};
    for (const bs of binanceSymbols) {
      const cgId = BINANCE_TO_COINGECKO[bs];
      if (cgId) {
        ids.push(cgId);
        idToBinance[cgId] = bs;
      }
    }
    if (ids.length === 0) {
      setLoading(false);
      return;
    }

    const fetchAll = async () => {
      try {
        const idsParam = ids.join(',');
        // Use markets endpoint — gives 24h change/high/low/volume in one call
        const url = `https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&ids=${idsParam}&price_change_percentage=24h`;
        const r = await fetch(url);
        if (!r.ok) throw new Error(`CoinGecko ${r.status}`);
        const data = await r.json();
        if (cancelled) return;

        const map = {};
        for (const coin of (Array.isArray(data) ? data : [])) {
          const bs = idToBinance[coin.id];
          if (!bs) continue;
          map[bs] = {
            price: coin.current_price || 0,
            change_24h_pct: coin.price_change_percentage_24h || 0,
            change_24h: coin.price_change_24h || 0,
            high_24h: coin.high_24h || 0,
            low_24h: coin.low_24h || 0,
            volume_24h: coin.total_volume || 0,
            quote_volume_24h: coin.total_volume || 0,
          };
        }
        setPrices(map);
        setError(null);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchAll();
    const id = setInterval(fetchAll, intervalMs);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(binanceSymbols), intervalMs]);

  return { prices, loading, error };
};

// Backward-compatible alias
export const useBinancePrices = useCryptoPrices;
